// The hero element. Custom SVG bar+line chart for 30-day burn.
// No chart-lib dep — keep bundle tiny. Animates on mount via stagger via CSS transitions.

import { useId, useMemo, useState } from 'react';
import type { BurnPoint } from '../lib/api';
import { fmtDate, fmtUsd, microsToUsd } from '../lib/format';

interface Props {
  points: BurnPoint[];
  allocatedCents: number;
  periodStart: number;
  periodEnd: number;
  consumedMicros: number;
}

const W = 720;
const H = 200;
const PAD_L = 8;
const PAD_R = 8;
const PAD_T = 12;
const PAD_B = 24;

export function BurnRateSparkline({ points, allocatedCents, periodStart, periodEnd, consumedMicros }: Props) {
  const idRoot = useId();
  const [hover, setHover] = useState<number | null>(null);

  const { bars, runoutX, ideal, maxMicros } = useMemo(() => {
    const allocatedMicros = allocatedCents * 1_000_000;
    const periodMs = periodEnd - periodStart;
    const elapsedMs = Math.min(periodMs, Date.now() - periodStart);
    const idealMicros = (elapsedMs / Math.max(1, periodMs)) * allocatedMicros;

    const max = Math.max(1, ...points.map((p) => p.micros));
    const usableW = W - PAD_L - PAD_R;
    const usableH = H - PAD_T - PAD_B;
    const days = Math.max(30, points.length);
    const bw = usableW / days;

    // Project runout: extrapolate average daily burn over last 7d
    const recent = points.slice(-7);
    const avgPerDay = recent.length === 0 ? 0 : recent.reduce((s, p) => s + p.micros, 0) / recent.length;
    const remainingMicros = Math.max(0, allocatedMicros - consumedMicros);
    const daysToRunout = avgPerDay <= 0 ? Infinity : remainingMicros / avgPerDay;
    const runoutMs = isFinite(daysToRunout) ? Date.now() + daysToRunout * 86_400_000 : null;
    const ranOutMs = runoutMs !== null && runoutMs < periodEnd ? runoutMs : null;

    return {
      bars: points.map((p, i) => ({
        x: PAD_L + i * bw,
        h: (p.micros / max) * usableH,
        bucket_start: p.bucket_start,
        micros: p.micros,
        events: p.events,
        w: Math.max(2, bw - 2),
      })),
      runoutX: ranOutMs === null ? null : PAD_L + ((ranOutMs - periodStart) / periodMs) * usableW,
      ideal: idealMicros,
      maxMicros: max,
    };
  }, [points, allocatedCents, periodStart, periodEnd, consumedMicros]);

  const allocatedMicros = allocatedCents * 1_000_000;
  const burnedPct = allocatedMicros === 0 ? 0 : (consumedMicros / allocatedMicros) * 100;
  const idealPct = allocatedMicros === 0 ? 0 : (ideal / allocatedMicros) * 100;
  const pace = burnedPct - idealPct;
  const paceColor = pace > 10 ? 'var(--alarm)' : pace > 3 ? 'var(--warn)' : 'var(--ok)';

  const hovered = hover !== null ? bars[hover] : null;

  return (
    <div className="card p-6">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[color:var(--mute)]">30-day burn</div>
          <div className="num text-4xl mt-1">{fmtUsd(microsToUsd(consumedMicros))}</div>
          <div className="text-xs text-[color:var(--mute)] mt-1">
            of {fmtUsd(allocatedCents / 100)} allocated · period {fmtDate(periodStart)}–{fmtDate(periodEnd)}
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-widest text-[color:var(--mute)]">Pace</div>
          <div className="num text-2xl" style={{ color: paceColor }}>
            {pace >= 0 ? '+' : ''}{pace.toFixed(1)}%
          </div>
          <div className="text-xs text-[color:var(--mute)] mt-1">
            vs ideal {idealPct.toFixed(0)}%
          </div>
        </div>
      </div>

      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto" role="img" aria-label="Burn-rate sparkline">
        <defs>
          <linearGradient id={`${idRoot}-fade`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.9" />
            <stop offset="100%" stopColor="var(--accent)" stopOpacity="0.2" />
          </linearGradient>
        </defs>

        {/* Ideal-pace marker */}
        <line
          x1={PAD_L} x2={W - PAD_R}
          y1={H - PAD_B - (ideal / Math.max(1, allocatedMicros)) * (H - PAD_T - PAD_B)}
          y2={H - PAD_B - (ideal / Math.max(1, allocatedMicros)) * (H - PAD_T - PAD_B)}
          stroke="var(--rule)" strokeDasharray="2 4"
        />

        {/* Bars */}
        {bars.map((b, i) => (
          <rect
            key={i}
            x={b.x}
            y={H - PAD_B - b.h}
            width={b.w}
            height={b.h}
            fill={`url(#${idRoot}-fade)`}
            style={{
              opacity: hover === null || hover === i ? 1 : 0.35,
              transition: 'opacity 120ms ease-out',
              cursor: 'pointer',
            }}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
          />
        ))}

        {/* Runout projection */}
        {runoutX !== null && (
          <line x1={runoutX} x2={runoutX} y1={PAD_T} y2={H - PAD_B} stroke="var(--alarm)" strokeWidth={1.5} />
        )}

        {/* x-axis ticks (sparse) */}
        {bars.length > 0 && [bars[0]!, bars[Math.floor(bars.length / 2)]!, bars[bars.length - 1]!].map((b, i) => (
          <text key={i} x={b.x} y={H - 6} fontSize={10} fill="var(--mute)" fontFamily="var(--mono)">
            {fmtDate(b.bucket_start)}
          </text>
        ))}
      </svg>

      {hovered && (
        <div className="mt-3 num text-sm flex gap-6 text-[color:var(--mute)]">
          <span>{fmtDate(hovered.bucket_start)}</span>
          <span>{fmtUsd(microsToUsd(hovered.micros))}</span>
          <span>{hovered.events} events</span>
          <span className="text-[color:var(--dim)]">/ max {fmtUsd(microsToUsd(maxMicros))}</span>
        </div>
      )}
    </div>
  );
}
